import React, { useState, useEffect } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Paper,
  Grid
} from '@mui/material';
import { Brain, ArrowLeft, ArrowRight, CheckCircle, Plus } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import InferenceStepBox from '../Boxes/Steps/InferenceStepBox';
import CodeStepBox from '../Boxes/Steps/CodeStepBox';
import ApiStepBox from '../Boxes/Steps/ApiStepBox';
import InferenceChainStep from '../../models/Steps/InferenceChainStep';
import CodeStep from '../../models/Steps/CodeStep';
import ApiStep from '../../models/Steps/ApiStep';
import { DEFAULT_MODEL, DEFAULT_TEMPERATURE } from '../../config/models';

interface CognitiveAbilityStepsModalProps {
  open: boolean;
  onClose: () => void;
  abilityType: 'learn' | 'retrieve' | 'reason' | 'design' | 'reflect';
  abilityData: { enabled: boolean; steps: any[] };
  onUpdate: (abilityType: string, data: { enabled: boolean; steps: any[] }) => void;
  workflowInputs: Record<string, any>;
  setWorkflowInputs: (inputs: Record<string, any>) => void;
}

const CognitiveAbilityStepsModal: React.FC<CognitiveAbilityStepsModalProps> = ({
  open,
  onClose,
  abilityType,
  abilityData,
  onUpdate,
  workflowInputs,
  setWorkflowInputs
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState(abilityData.steps || []);

  const abilityConfig = {
    learn: {
      title: 'Learning Ability',
      icon: '📚',
      description: 'Configure learning and knowledge acquisition steps',
      color: '#52c41a'
    },
    retrieve: {
      title: 'Retrieval Ability',
      icon: '🔍',
      description: 'Configure information retrieval and search steps',
      color: '#1890ff'
    },
    reason: {
      title: 'Reasoning Ability',
      icon: '🧠',
      description: 'Configure logical reasoning and inference steps',
      color: '#722ed1'
    },
    design: {
      title: 'Design Ability',
      icon: '🎨',
      description: 'Configure creative design and planning steps',
      color: '#eb2f96'
    },
    reflect: {
      title: 'Reflection Ability',
      icon: '🪞',
      description: 'Configure self-reflection and evaluation steps',
      color: '#fa8c16'
    }
  };

  const config = abilityConfig[abilityType];

  const wizardSteps = [
    { label: 'Overview', description: `Configure ${config.title.toLowerCase()}` },
    { label: 'Steps', description: 'Add and configure processing steps' },
    { label: 'Review', description: 'Review and save configuration' }
  ];

  useEffect(() => {
    setSteps(abilityData.steps || []);
  }, [abilityData]);

  const addStep = (type: 'inference_chain' | 'code' | 'api') => {
    let newStep;
    
    switch (type) {
      case 'inference_chain':
        newStep = new InferenceChainStep(
          `${config.title} Inference Step`,
          {
            model: DEFAULT_MODEL,
            model_temp: DEFAULT_TEMPERATURE,
            prompt: `You are performing ${abilityType} operations. Please process the following:`,
            variables: {},
            retriever: null,
            is_streaming: false
          }
        );
        break;
      case 'code':
        newStep = new CodeStep(
          `${config.title} Code Step`,
          {
            code: `# ${config.title} processing code\nprint("Processing ${abilityType} operation")`,
            variables: {}
          }
        );
        break;
      case 'api':
        newStep = new ApiStep(
          `${config.title} API Step`,
          {
            type: 'GET',
            endpoint: '',
            headers: {},
            params: {},
            body: {}
          }
        );
        break;
    }

    setSteps(prev => [...prev, newStep]);
  };

  const removeStep = (id: string) => {
    setSteps(prev => prev.filter(step => step.id !== id));
  };

  const handleStepUpdate = (updatedStep: any) => {
    setSteps(prevSteps =>
      prevSteps.map(step => step.id === updatedStep.id ? updatedStep : step)
    );
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(steps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSteps(items);
  };

  const handleNext = () => {
    if (currentStep < wizardSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = () => {
    onUpdate(abilityType, {
      enabled: true,
      steps: steps
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Box sx={{ 
          fontSize: '1.5rem',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: config.color,
          color: 'white'
        }}>
          {config.icon}
        </Box>
        <Typography variant="h5" fontWeight="bold" sx={{ color: config.color }}>
          {config.title} Configuration
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ py: 3 }}>
        <Stepper 
          activeStep={currentStep} 
          alternativeLabel
          sx={{ mb: 4 }}
        >
          {wizardSteps.map((step, index) => (
            <Step 
              key={step.label} 
              completed={index < currentStep}
              onClick={() => setCurrentStep(index)}
              sx={{ cursor: 'pointer' }}
            >
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ minHeight: 400 }}>
          {currentStep === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h2" sx={{ mb: 2 }}>{config.icon}</Typography>
                  <Typography variant="h4" sx={{ mb: 1, color: config.color }}>
                    {config.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {config.description}
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Ability Description</Typography>
                  <Box sx={{ p: 2 }}>
                    {abilityType === 'learn' && (
                      <Typography variant="body1" paragraph>
                        <strong>Learning Ability</strong> enables the agent to acquire new knowledge,
                        adapt to new information, and improve its performance over time. Configure steps
                        that help the agent process and internalize new information.
                      </Typography>
                    )}
                    {abilityType === 'retrieve' && (
                      <Typography variant="body1" paragraph>
                        <strong>Retrieval Ability</strong> allows the agent to search for and access
                        relevant information from various sources. Configure steps for querying databases,
                        searching documents, or accessing external APIs.
                      </Typography>
                    )}
                    {abilityType === 'reason' && (
                      <Typography variant="body1" paragraph>
                        <strong>Reasoning Ability</strong> enables logical thinking, inference, and
                        problem-solving. Configure steps that help the agent analyze information,
                        draw conclusions, and make decisions.
                      </Typography>
                    )}
                    {abilityType === 'design' && (
                      <Typography variant="body1" paragraph>
                        <strong>Design Ability</strong> facilitates creative thinking, planning, and
                        solution design. Configure steps for brainstorming, prototyping, and creating
                        innovative solutions.
                      </Typography>
                    )}
                    {abilityType === 'reflect' && (
                      <Typography variant="body1" paragraph>
                        <strong>Reflection Ability</strong> enables self-assessment, learning from
                        experience, and continuous improvement. Configure steps for evaluating performance
                        and identifying areas for enhancement.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
          
          {currentStep === 1 && (
            <Box>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{config.title} Steps</Typography>
                                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'blue.200',
                        '&:hover': {
                          borderColor: 'blue.400',
                          bgcolor: 'blue.50'
                        },
                        transition: 'all 0.2s',
                        flex: 1
                      }}
                      onClick={() => addStep('inference_chain')}
                    >
                      <CardContent sx={{ p: 1, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ 
                            width: 24, 
                            height: 24, 
                            bgcolor: 'blue.100', 
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography variant="body2">🧠</Typography>
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'blue.700' }}>
                            Inference
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'green.200',
                        '&:hover': {
                          borderColor: 'green.400',
                          bgcolor: 'green.50'
                        },
                        transition: 'all 0.2s',
                        flex: 1
                      }}
                      onClick={() => addStep('code')}
                    >
                      <CardContent sx={{ p: 1, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ 
                            width: 24, 
                            height: 24, 
                            bgcolor: 'green.100', 
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography variant="body2">📝</Typography>
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'green.700' }}>
                            Code
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'orange.200',
                        '&:hover': {
                          borderColor: 'orange.400',
                          bgcolor: 'orange.50'
                        },
                        transition: 'all 0.2s',
                        flex: 1
                      }}
                      onClick={() => addStep('api')}
                    >
                      <CardContent sx={{ p: 1, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ 
                            width: 24, 
                            height: 24, 
                            bgcolor: 'orange.100', 
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography variant="body2">🔌</Typography>
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'orange.700' }}>
                            API
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                  </Box>
                  
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="cognitive-steps">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {steps.map((step, index) => (
                              <Draggable key={step.id} draggableId={step.id} index={index}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                                      {step.type === 'inference_chain' && (
                                        <InferenceStepBox
                                          stepData={step}
                                          onUpdate={handleStepUpdate}
                                          workflowInputs={workflowInputs}
                                          setWorkflowInputs={setWorkflowInputs}
                                        />
                                      )}
                                      {step.type === 'code' && (
                                        <CodeStepBox
                                          stepData={step}
                                          onUpdate={handleStepUpdate}
                                          workflowInputs={workflowInputs}
                                        />
                                      )}
                                      {step.type === 'api' && (
                                        <ApiStepBox
                                          stepData={step}
                                          onUpdate={handleStepUpdate}
                                          workflowInputs={workflowInputs}
                                          setWorkflowInputs={setWorkflowInputs}
                                        />
                                      )}
                                      <Button
                                        color="error"
                                        size="small"
                                        onClick={() => removeStep(step.id)}
                                        sx={{ mt: 1 }}
                                      >
                                        Remove Step
                                      </Button>
                                    </Paper>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Box>
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>

                  {steps.length === 0 && (
                    <Box sx={{ 
                      textAlign: 'center', 
                      py: 4, 
                      bgcolor: 'grey.50', 
                      borderRadius: 1,
                      border: '2px dashed',
                      borderColor: 'grey.300'
                    }}>
                      <Typography variant="h3" sx={{ mb: 2 }}>{config.icon}</Typography>
                      <Typography variant="body1" color="text.secondary" gutterBottom>
                        No steps configured yet. Add steps to enable this cognitive ability.
                      </Typography>
                      
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button 
                          variant="contained" 
                          color="primary" 
                          startIcon={<Plus size={16} />}
                          onClick={() => addStep('inference_chain')}
                        >
                          Add Inference Step
                        </Button>
                        <Button 
                          variant="outlined" 
                          startIcon={<Plus size={16} />}
                          onClick={() => addStep('code')}
                        >
                          Add Code Step
                        </Button>
                        <Button 
                          variant="outlined" 
                          startIcon={<Plus size={16} />}
                          onClick={() => addStep('api')}
                        >
                          Add API Step
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
          
          {currentStep === 2 && (
            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Configuration Summary</Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box sx={{ 
                      fontSize: '2rem',
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      bgcolor: config.color,
                      color: 'white'
                    }}>
                      {config.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ color: config.color, fontWeight: 'bold' }}>
                        {config.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {config.description}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="bold">Status:</Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip 
                          label="Enabled" 
                          color="success" 
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="bold">Steps Count:</Typography>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {steps.length} steps configured
                      </Typography>
                    </Box>
                  </Box>
                  
                  {steps.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body1" fontWeight="bold" gutterBottom>
                        Configured Steps:
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {steps.map((step, index) => (
                          <Box key={step.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={index + 1} color="primary" size="small" />
                            <Typography variant="body2" fontWeight="medium">
                              {step.description}
                            </Typography>
                            <Chip 
                              label={step.type} 
                              size="small"
                              color={
                                step.type === 'inference_chain' ? 'primary' :
                                step.type === 'code' ? 'success' : 'warning'
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={handlePrev}
          disabled={currentStep === 0}
          startIcon={<ArrowLeft size={16} />}
          variant="outlined"
        >
          Previous
        </Button>
        
        <Box sx={{ flexGrow: 1 }} />
        
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        
        {currentStep < wizardSteps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowRight size={16} />}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<CheckCircle size={16} />}
            color="success"
          >
            Save Ability
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CognitiveAbilityStepsModal;